import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductSeat from '../ProductCard/ProductSeat';
import Money from '../Common/Money/Money';
import ButtonReservation from '../Common/Button/ButtonReservation';
import { t } from 'i18next';
import ChevronIcon from '../icons/chevronIcon';
import { format } from 'date-fns';
import { setReservation } from '../../actions/reservationActions';
import { getQueryString } from '../../helpers/url';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { addExtraProduct, removeExtraProduct } from '../../actions/extraProductActions';

const SeatMap = ({ seatMapConfig, products, eventDetail }) => {
  const language = useSelector((state) => state.app.language);
  const currencies = useSelector((state) => state.currency);
  const currencyToShow = currencies.selected;
  const symbol = currencyToShow?.symbol;
  const convertCurrency = (value) => value * currencyToShow.exchangeRate;
  const [selectedProducts, setSelectedProduct] = useState([]);
  const chartRef = useRef(null);
  const reservationType = selectedProducts.length > 0 ? selectedProducts[0].reservationType : null;
  const history = useHistory();
  const dispatch = useDispatch();

  const idReservation = getQueryString('id');
  const showId = getQueryString('showId');
  const eventId = getQueryString('eventId');

  const pricing = products.map((product) => ({
    category: product.locationCode,
    price: convertCurrency(product.prices.price),
  }));

  const priceFormatter = (price) => {
    return `${symbol}${price.toFixed(2)}`;
  };

  const queryParams = new URLSearchParams({
    eventId: eventId,
    showId: showId,
  });

  if (idReservation !== null && idReservation !== undefined) {
    queryParams.append('id', idReservation);
  }
  let query = `?${queryParams.toString()}`;

  const setReservationInfo = () => {
    const productMap = [];

    selectedProducts.forEach((product) => {
      productMap.push({
        id: product.id,
        quantity: 1,
        extraPaxs: 0,
        specialType: product.specialType,
        paxs: product.pax,
        seats: product.seatProps.id,
      });
    });

    const reservationInfo = {
      showId: showId,
      showName: eventDetail.name,
      products: productMap,
      date: format(new Date(eventDetail.startDate), 'yyyy-MM-dd'),
      hasTransportation: eventDetail.hasTransportation,
      performance: format(new Date(eventDetail.startDate), 'HH:mm'),
      paxs: productMap.reduce((sum, item) => sum + item.paxs, 0),
    };

    return reservationInfo;
  };

  const handleSelect = (item) => {
    setSelectedProduct((prevSelectedProducts) => {
      const alreadySelected = prevSelectedProducts.some((p) => p.seatProps.id === item.id);
      if (alreadySelected) return prevSelectedProducts;

      const product = products.find((p) => p.locationCode === item.category.key.toString());

      return [...prevSelectedProducts, { ...product, seatProps: { ...item } }];
    });
  };

  const handleDeselect = (id) => {
    if (chartRef.current) {
      chartRef.current.deselectObjects([id]);
    }

    //dispatch(removeExtraProduct({ ...newProducts[index], quantity: 0, specialType: 'EXTRAPAX' }));

    setSelectedProduct((prev) => prev.filter((p) => p.seatProps.id !== id));
  };

  const subTitle = (product) => {
    let result = product.seatProps.label;

    if (product.pax > 1) result += ' - ' + t('upToGuests', { pax: product.pax });

    return result;
  };

  const renderProducts = () => {
    return (
      <div className='mt-4'>
        {selectedProducts.map((p) => (
          <ProductSeat
            key={p.id}
            product={p}
            subTitle={subTitle(p)}
            handleRemove={(e) => handleDeselect(e)}
          />
        ))}
      </div>
    );
  };

  const sumTotal = () => {
    return selectedProducts.reduce((total, p) => total + p.prices.price, 0);
  };

  const totalConsume = () => {
    return selectedProducts.reduce((total, p) => total + p.prices.consumption, 0);
  };

  const totalPax = () => {
    return selectedProducts.reduce((total, p) => total + p.pax, 0);
  };

  const handleContinue = () => {
    const reservationInfo = setReservationInfo();
    dispatch(setReservation(reservationInfo));
    
    if (selectedProducts.some((p) => p.extraPax > 0)) {
      selectedProducts
        .filter((x) => x.extraPax > 0)
        .map((item) => {
          dispatch(addExtraProduct({ ...item, quantity: 0, specialType: 'EXTRAPAX' }));
        });

      history.push({
        pathname: '/area-extra-reservation',
        search: query,
      });
    } else if (eventDetail.hasTransportation) {
      history.push({
        pathname: '/pickup',
        search: query,
      });
    } else {
      history.push({
        pathname: '/reservation-check-tickets',
        search: query,
      });
    }
  };

  return (
    <>
      <SeatsioSeatingChart
        onChartRendered={(chart) => {
          chartRef.current = chart;
        }}
        workspaceKey={seatMapConfig.workspacePublicKey}
        event={seatMapConfig.eventId}
        region='na'
        pricing={pricing}
        priceFormatter={priceFormatter}
        onObjectSelected={(object) => handleSelect(object)}
        onObjectDeselected={(object) => {
          handleDeselect(object.id);
        }}
        language={language}
        showFullScreenButton={false}
        showZoomOutButtonOnMobile={false}
      />
      <div>{renderProducts()}</div>
      {selectedProducts.length > 0 && (
        <div
          className='position-fixed w-100'
          style={{
            bottom: 0,
            left: 0,
            zIndex: 2,
          }}
        >
          <div className='container'>
            <div className='row justify-content-center'>
              <div className='col-12 col-md-7 col-lg-6 p-0'>
                <ButtonReservation
                  leftContent={
                    <>
                      <div className='font-normal'>
                        {t('total')} <Money value={sumTotal()} />
                      </div>
                      <div className='font-smaller'>
                        {reservationType.toUpperCase() === 'AREA'
                          ? t('upToGuests', { pax: totalPax() })
                          : t('person', { count: totalPax() })}
                      </div>
                      {totalConsume() > 0 && (
                        <div className='font-smaller'>
                          {t('consumptionCredit')} <Money value={totalConsume()} />
                        </div>
                      )}
                    </>
                  }
                  rightContent={
                    <div>
                      {t('continue')}
                      <span>
                        <ChevronIcon size={20} fillColor={'#000'} />
                      </span>
                    </div>
                  }
                  size='large'
                  className={`btn-primary`}
                  onClick={handleContinue}
                  fixed={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SeatMap;
